import React from "react";
import { useState } from "react";
import { FiMail, FiMapPin, FiSend, FiUser, FiMessageCircle } from "react-icons/fi";

function Contact() {
    const [formData, setFormData] = useState({ name: "", email: "", message: "" });
    const [sent, setSent] = useState(false);
    const [error, setError] = useState("");
    const [isSubmitting, setIsSubmitting] = useState(false);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError("");
        setIsSubmitting(true);

        try {
            // In a real app, you'd send this to your backend API
            const response = await fetch('/api/contact', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(formData),
            });

            if (response.ok) {
                setSent(true);
                setFormData({ name: "", email: "", message: "" });
            } else {
                setError("Failed to send message. Please try again or email us directly.");
            }
        } catch (err) {
            console.error('Contact form error:', err);
            setError("Failed to send message. Please try again or email us directly.");
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div className="min-h-screen pt-20 pb-24">
            {/* Hero Banner */}
            <div className="relative bg-gradient-to-br from-[#6366f1] via-[#8b5cf6] via-[#a855f7] to-[#ec4899] overflow-hidden rounded-b-3xl border-b-4 border-[#fbbf24]/60 shadow-2xl shadow-[#8b5cf6]/50">
                {/* Cosmic nebula overlay */}
                <div className="absolute inset-0 bg-gradient-to-tr from-[#3b82f6]/20 via-transparent to-[#f472b6]/20"></div>
                <div className="absolute inset-0 bg-gradient-to-bl from-[#fbbf24]/10 via-transparent to-[#06b6d4]/10"></div>
                
                {/* Space decorations */}
                <div className="absolute top-4 left-4 text-4xl animate-bounce-joy">📧</div>
                <div className="absolute top-8 right-8 text-3xl animate-float">💬</div>
                <div className="absolute bottom-4 left-8 text-3xl animate-wiggle">✉️</div>
                <div className="absolute bottom-8 right-4 text-4xl animate-bounce-joy" style={{ animationDelay: '1s' }}>✨</div>
                <div className="absolute top-1/2 left-1/4 text-2xl animate-twinkle">💫</div>
                <div className="absolute top-1/3 right-1/4 text-2xl animate-float" style={{ animationDelay: '0.5s' }}>📮</div>
                
                <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16 relative z-10 text-center">
                    <div className="inline-block p-4 bg-white/20 rounded-full mb-6 backdrop-blur-sm">
                        <FiMessageCircle className="text-white text-5xl drop-shadow-lg" />
                    </div>
                    <h1 className="text-4xl md:text-5xl font-extrabold text-white mb-4 drop-shadow-lg animate-bounce-joy bg-gradient-to-r from-[#fef3c7] via-white to-[#fef3c7] bg-clip-text text-transparent">
                        Contact Us
                    </h1>
                    <div className="w-20 h-1 bg-gradient-to-r from-[#fbbf24] via-[#ec4899] to-[#3b82f6] mb-4 mx-auto rounded-full shadow-lg shadow-[#8b5cf6]/60"></div>
                    <p className="text-white/95 text-lg max-w-2xl mx-auto">
                        To get in touch with the team, please fill out the form below. We'll get back to you as soon as possible.
                    </p>
                </div>
            </div>

            <main className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 mt-12">
                <div className="grid md:grid-cols-2 gap-8">
                    {/* Contact Form */}
                    <div className="bg-gradient-to-br from-white/10 via-white/5 to-transparent backdrop-blur-sm rounded-2xl p-6 md:p-8 border border-white/20 shadow-xl">
                        <h2 className="text-2xl font-bold mb-6 text-white flex items-center gap-2">
                            <FiMessageCircle className="text-white" />
                            Send us a Message
                        </h2>

                        {!sent ? (
                            <>
                                {error && (
                                    <div className="mb-4 p-4 bg-red-700/30 border-2 border-red-500/50 rounded-lg text-white text-sm">
                                        {error}
                                    </div>
                                )}
                                <form onSubmit={handleSubmit} className="space-y-4">
                                    <div>
                                        <label className="block text-sm font-medium text-white mb-2">
                                            Your Name
                                        </label>
                                        <div className="relative">
                                            <FiUser className="absolute left-3 top-1/2 transform -translate-y-1/2 text-white/70" />
                                            <input
                                                type="text"
                                                value={formData.name}
                                                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                                className="w-full pl-10 pr-4 py-3 rounded-lg bg-white/20 border border-white/40 focus:border-white/60 focus:outline-none focus:ring-2 focus:ring-white/30 transition text-white placeholder-white/50"
                                                placeholder="John Doe"
                                                required
                                            />
                                        </div>
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-white mb-2">
                                            Email Address
                                        </label>
                                        <div className="relative">
                                            <FiMail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-white/70" />
                                            <input
                                                type="email"
                                                value={formData.email}
                                                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                                                className="w-full pl-10 pr-4 py-3 rounded-lg bg-white/20 border border-white/40 focus:border-white/60 focus:outline-none focus:ring-2 focus:ring-white/30 transition text-white placeholder-white/50"
                                                placeholder="john@example.com"
                                                required
                                            />
                                        </div>
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-white mb-2">
                                            Message
                                        </label>
                                        <textarea
                                            value={formData.message}
                                            onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                                            className="w-full px-4 py-3 rounded-lg bg-white/20 border border-white/40 focus:border-white/60 focus:outline-none focus:ring-2 focus:ring-white/30 transition resize-none text-white placeholder-white/50"
                                            placeholder="Tell us how we can help..."
                                            rows={6}
                                            required
                                        />
                                    </div>

                                    <button
                                        type="submit"
                                        disabled={isSubmitting}
                                        className="w-full px-6 py-3 bg-gradient-to-r from-[#6366f1] to-[#8b5cf6] hover:from-[#8b5cf6] hover:to-[#a855f7] disabled:from-[#4b5563] disabled:to-[#6b7280] disabled:cursor-not-allowed rounded-lg transition font-semibold flex items-center justify-center gap-2 text-white shadow-lg shadow-[#8b5cf6]/30"
                                    >
                                        {isSubmitting ? (
                                            <>
                                                <span className="animate-spin">⏳</span>
                                                Sending...
                                            </>
                                        ) : (
                                            <>
                                                <FiSend />
                                                Send Message
                                            </>
                                        )}
                                    </button>
                                </form>
                            </>
                        ) : (
                            <div className="text-center py-8">
                                <div className="inline-block p-4 bg-green-700/20 border-2 border-green-500/50 rounded-full mb-4">
                                    <svg className="w-12 h-12 text-green-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                    </svg>
                                </div>
                                <h3 className="text-2xl font-bold mb-2 text-white">Message Sent!</h3>
                                <p className="text-white/90 mb-6">
                                    Thanks for reaching out. We'll reply shortly.
                                </p>
                                <button
                                    onClick={() => {
                                        setSent(false);
                                        setError("");
                                    }}
                                    className="px-4 py-2 bg-white/20 hover:bg-white/30 border border-white/40 rounded-lg transition text-sm font-semibold text-white"
                                >
                                    Send Another Message
                                </button>
                            </div>
                        )}
                    </div>

                    {/* Contact Information */}
                    <div className="space-y-6">
                        <div className="bg-gradient-to-br from-white/10 via-white/5 to-transparent backdrop-blur-sm rounded-2xl p-6 md:p-8 border border-white/20 shadow-xl">
                            <h2 className="text-2xl font-bold mb-6 text-white">Get in Touch</h2>
                            <div className="space-y-6">
                                {/* Email */}
                                <div className="flex items-start gap-4">
                                    <div className="p-3 bg-white/20 rounded-lg border border-white/40">
                                        <FiMail className="text-white text-xl" />
                                    </div>
                                    <div>
                                        <h3 className="font-semibold text-white mb-1">Email</h3>
                                        <a
                                            href="mailto:contact@joyloom.com"
                                            className="text-white/90 hover:text-white transition underline"
                                        >
                                            contact@joyloom.com
                                        </a>
                                        <p className="text-sm text-white/70 mt-1">
                                            We typically respond within 24-48 hours
                                        </p>
                                    </div>
                                </div>

                                {/* Address */}
                                <div className="flex items-start gap-4">
                                    <div className="p-3 bg-white/20 rounded-lg border border-white/40">
                                        <FiMapPin className="text-white text-xl" />
                                    </div>
                                    <div>
                                        <h3 className="font-semibold text-white mb-1">Office Location</h3>
                                        <p className="text-white/90">
                                            28 Brobänksvägen, Tyresö, Stockholm County<br />
                                            Sweden
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Additional Info */}
                        <div className="bg-gradient-to-br from-white/10 via-white/5 to-transparent backdrop-blur-sm rounded-xl p-6 border border-white/20 shadow-xl">
                            <h3 className="font-semibold text-white mb-3">Other Ways to Reach Us</h3>
                            <ul className="space-y-2 text-white/90 text-sm">
                                <li className="flex items-start gap-2">
                                    <span className="text-white mt-1">•</span>
                                    <span>For press inquiries, please mention "Press" in your subject line</span>
                                </li>
                                <li className="flex items-start gap-2">
                                    <span className="text-white mt-1">•</span>
                                    <span>For partnership opportunities, include "Partnership" in your message</span>
                                </li>
                                <li className="flex items-start gap-2">
                                    <span className="text-white mt-1">•</span>
                                    <span>For support related to our games, use the in-game support feature</span>
                                </li>
                            </ul>
                        </div>
                    </div>
                </div>
            </main>
        </div>
    );
}

export default Contact;
